import { APP_BASE_HREF, CommonModule, CurrencyPipe } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { MAT_MOMENT_DATE_FORMATS } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatExpansionPanelDefaultOptions, MAT_EXPANSION_PANEL_DEFAULT_OPTIONS } from '@angular/material/expansion';
import { MatFormFieldDefaultOptions, MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatPaginatorDefaultOptions, MAT_PAGINATOR_DEFAULT_OPTIONS } from '@angular/material/paginator';
import { MatTabsConfig, MAT_TABS_CONFIG } from '@angular/material/tabs';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { StingrayCommonModule } from '@modules/common/common.module';
import { ErrorHandlerService } from '@modules/common/services/error-handler.service';
import { MomentUtcDateAdapter } from '@modules/common/services/moment-utc-date-adapter';
import { UserSessionService } from '@modules/common/services/user-session.service';
import { StingrayRoutingModule } from '@modules/routing/routing.module';
import { DragulaModule } from 'ng2-dragula';
import { AppComponent } from 'src/app/app.component';

/**
 * Application-wide default options for mat-form-field elements.
 */
const MAT_FORM_FIELD_OPTIONS: MatFormFieldDefaultOptions = {
    appearance: 'fill',
    floatLabel: 'always'
};

/**
 * Application-wide default options for mat-tab-group elements.
 */
const MAT_TAB_GROUP_OPTIONS: MatTabsConfig = {
    animationDuration: '0ms'
};

/**
 * Application-wide default options for mat-paginator elements.
 */
const MAT_PAGINATOR_OPTIONS: MatPaginatorDefaultOptions = {
    formFieldAppearance: 'fill'
};

/**
 * Application-wide default options for mat-expansion-panel and related elements.
 */
const MAT_EXPANSION_PANEL_OPTIONS: MatExpansionPanelDefaultOptions = {
    hideToggle: true,
    collapsedHeight: '',
    expandedHeight: ''
};

@NgModule({
    bootstrap: [AppComponent],
    declarations: [
        AppComponent
    ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        CommonModule,
        HttpClientModule,
        RouterModule,
        StingrayCommonModule,
        StingrayRoutingModule,
        DragulaModule.forRoot()
    ],
    providers: [
        { provide: 'BASE_URL', useFactory: getBaseUrl },
        { provide: APP_BASE_HREF, useFactory: getBaseHref },
        { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
        { provide: DateAdapter, useClass: MomentUtcDateAdapter },
        {
            provide: APP_INITIALIZER,
            useFactory: appInitializer,
            multi: true,
            deps: [UserSessionService]
        },
        { provide: ErrorHandler, useClass: ErrorHandlerService },
        CurrencyPipe,
        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: MAT_FORM_FIELD_OPTIONS },
        { provide: MAT_TABS_CONFIG, useValue: MAT_TAB_GROUP_OPTIONS },
        { provide: MAT_PAGINATOR_DEFAULT_OPTIONS, useValue: MAT_PAGINATOR_OPTIONS },
        { provide: MAT_EXPANSION_PANEL_DEFAULT_OPTIONS, useValue: MAT_EXPANSION_PANEL_OPTIONS }
    ]
})
export class AppModule {
}

export function appInitializer(userSessionService: UserSessionService): () => Promise<any> {
    return (): Promise<any> => {
        return userSessionService.initialize();
    };
}

export function getBaseUrl() {
    return document.getElementsByTagName('base')[0].href;
}

function getBaseHref(): string {
    //The actual <base> tag's href property is set by the --base-href option in ng build. That property
    //must be set to the CDN because that's where we upload all of the static assets (.js and .css files).
    //This function is to return the base used for routing. It has to include the scheme and authority
    //of the URL so that it works for both ng serve (development) and ng build (deployed site).
    //This functionality used to be provided by the --deploy-url option of ng build, but that property
    //was deprecated without a real replacement, so now we have to work around it.
    return window.location.origin;
}
